// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="TagsInput" set="mytaible-shadcn-studio" verified=tbd

import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TagsInputProps {
  /**
   * Controlled list of tags currently displayed as chips.
   */
  value: string[];

  /**
   * Callback fired whenever the tag list changes — receives the new array.
   */
  onChange: (tags: string[]) => void;

  /**
   * Placeholder shown inside the text field when no tags are present.
   * Figma: hint text inside the input area.
   */
  placeholder?: string;

  /**
   * Disables the entire control — chips lose their remove button, the text
   * input is non-interactive, and the wrapper is rendered at 50% opacity.
   */
  disabled?: boolean;

  /**
   * Puts the control into read-only mode — chips are visible but not
   * removable; the text input is non-editable.
   */
  readOnly?: boolean;

  /**
   * Upper bound on the number of tags allowed. Adding tags beyond this limit
   * (by typing, pressing Enter/comma, or pasting) is silently ignored.
   */
  maxTags?: number;

  /** Additional CSS classes merged onto the outer wrapper `<div>`. */
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Multi-value chip/tag input. Users type text and press **Enter** or **comma**
 * to create a tag; **Backspace** on an empty field removes the last tag.
 * Pasting comma-separated text adds each segment as a separate tag in one
 * operation.
 *
 * Tags are displayed as removable chips inside the input border. Duplicate tags
 * are silently rejected. The component is fully controlled — the caller owns
 * the `value` array.
 *
 * Figma: `pkCGby54zXzTsccXvHqzO6` node `tbd` — component set `tbd`.
 *
 * @example
 * // Basic controlled usage
 * const [tags, setTags] = React.useState<string[]>([]);
 * <TagsInput value={tags} onChange={setTags} placeholder="Add technology…" />
 *
 * @example
 * // With a cap
 * <TagsInput value={tags} onChange={setTags} maxTags={5} />
 *
 * @example
 * // Disabled — chips visible, not removable
 * <TagsInput value={['react', 'typescript']} onChange={() => {}} disabled />
 *
 * @example
 * // ReadOnly — same visual; text field locked
 * <TagsInput value={['read', 'only']} onChange={() => {}} readOnly />
 */
const TagsInput = React.forwardRef<HTMLInputElement, TagsInputProps>(
  ({ value, onChange, placeholder = 'Add tag…', disabled, readOnly, maxTags, className }, ref) => {
    const [inputValue, setInputValue] = React.useState('');

    // ── Tag management ─────────────────────────────────────────────────────────

    const addTag = (raw: string) => {
      const tag = raw.trim();
      if (!tag || value.includes(tag)) return;
      if (maxTags !== undefined && value.length >= maxTags) return;
      onChange([...value, tag]);
    };

    const removeTag = (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    };

    // ── Keyboard handling ──────────────────────────────────────────────────────

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if ((e.key === 'Enter' || e.key === ',') && !e.nativeEvent.isComposing) {
        e.preventDefault();
        addTag(inputValue);
        setInputValue('');
      } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
        removeTag(value.length - 1);
      }
    };

    // ── Paste — comma/newline-delimited bulk add ───────────────────────────────

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text');
      const parts = text
        .split(/[,\n]+/)
        .map((t) => t.trim())
        .filter(Boolean);

      if (parts.length > 1) {
        e.preventDefault();
        const seen = new Set(value);
        const next = [...value];
        for (const t of parts) {
          if (!seen.has(t)) {
            seen.add(t);
            next.push(t);
          }
        }
        onChange(maxTags !== undefined ? next.slice(0, maxTags) : next);
        setInputValue('');
      }
    };

    // ── Blur — commit any in-progress text ────────────────────────────────────

    const handleBlur = () => {
      if (inputValue.trim()) {
        addTag(inputValue);
        setInputValue('');
      }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
      <div
        className={cn(
          // ── Layout ──────────────────────────────────────────────────────────
          'flex min-h-[36px] w-full flex-wrap items-center gap-1.5',
          // ── Figma: borderRadius → --field-radius token (falls back to --radius) ─
          'rounded-[var(--field-radius,var(--radius))]',
          // ── Figma: stroke_US95ZD (border-input) + fill_4P148K (bg-input-bg) ─────
          'border border-input bg-input-bg px-3 py-1.5 text-base shadow-sm sm:text-sm',
          // ── Focus ring — matches Input/Select/Textarea pattern ───────────────────
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring',
          'focus-within:ring-offset-2 focus-within:ring-offset-background',
          // ── Error state — set aria-invalid on a wrapping element to activate ──────
          'aria-[invalid=true]:border-destructive',
          // ── Disabled — Figma: opacity 0.5, pointer-events blocked ─────────────────
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        {/* ── Tag chips ──────────────────────────────────────────────────────── */}
        {value.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
          >
            {tag}
            {!disabled && !readOnly && (
              <button
                type="button"
                tabIndex={-1}
                onClick={() => removeTag(i)}
                aria-label={`Remove ${tag}`}
                className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none"
              >
                <X className="size-3" />
              </button>
            )}
          </span>
        ))}

        {/* ── Text input ─────────────────────────────────────────────────────── */}
        <input
          ref={ref}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : undefined}
          disabled={disabled}
          readOnly={readOnly}
          aria-label="Add tag"
          className="min-w-[120px] flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed sm:text-sm"
        />
      </div>
    );
  },
);
TagsInput.displayName = 'TagsInput';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { TagsInput };
